import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, ShoppingCart, LogOut, Store, Phone, MapPin, UserCheck, ArrowLeft, Package, Tag, ClipboardList, PlusCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import BrandsSection from "@/components/customer/BrandsSection";
import ProductsSection from "@/components/customer/ProductsSection";
import CartDrawer from "@/components/customer/CartDrawer";
import StaffMyOrders from "@/components/staff/StaffMyOrders";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  blocked: "bg-red-100 text-red-700",
};

export default function Staff() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { count, clear } = useCart();
  const [phone, setPhone] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [myOrdersKey, setMyOrdersKey] = useState(0);

  // History-backed screen model so Android system/gesture Back unwinds Salesman screens
  // instead of leaving the app. Every drill-down pushes a browser history entry.
  const BASE = { view: "order", custId: null, tab: "brands", brand: null };
  const custCache = useRef({});
  const screenRef = useRef(BASE);
  const [screen, setScreen] = useState(BASE);

  const view = screen.view;
  const customer = screen.custId ? custCache.current[screen.custId] || null : null;
  const tab = screen.tab;
  const brandFilter = screen.brand;

  useEffect(() => {
    window.history.replaceState({ slmStaff: true, screen: screenRef.current }, "");
    const onPop = (e) => {
      const s = e.state && e.state.slmStaff ? e.state.screen : BASE;
      screenRef.current = s;
      setScreen(s);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const nav = (patch, { replace = false } = {}) => {
    const next = { ...screenRef.current, ...patch };
    screenRef.current = next;
    setScreen(next);
    const st = { slmStaff: true, screen: next };
    if (replace) window.history.replaceState(st, "");
    else window.history.pushState(st, "");
  };

  const handleLogout = async () => {
    clear();
    await logout();
    navigate("/login", { replace: true });
  };

  const search = async (e) => {
    e?.preventDefault();
    setError(""); setSearching(true); setResults(null);
    try {
      const { data } = await api.get("/staff/customers/search", { params: { phone } });
      setResults(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSearching(false); }
  };

  const selectCustomer = (c) => {
    clear();
    custCache.current[c.id] = c;
    nav({ view: "order", custId: c.id, tab: "brands", brand: null });
  };

  const changeCustomer = () => {
    clear();
    setResults(null);
    setPhone("");
    nav({ view: "order", custId: null, tab: "brands", brand: null });
  };

  const goProductsForBrand = (brand) => {
    nav({ view: "order", tab: "products", brand }); // push -> Back returns to Our Brands
  };

  const selectTab = (k) => {
    // tab switches replace the current entry (only brand drill-down adds history)
    nav({ tab: k, brand: k === "products" ? screenRef.current.brand : null }, { replace: true });
  };

  const goMyOrders = () => {
    nav({ view: "myorders" }); // push -> Back returns to previous Salesman screen
    setMyOrdersKey((k) => k + 1);
  };

  const goNewOrder = () => {
    if (screenRef.current.view !== "order") nav({ view: "order" });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50/60 to-white">
      <header className="sticky top-0 z-30 border-b border-blue-100 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex items-center gap-3">
            <img src="/slm-logo.png" alt="SLM" className="h-11 w-auto object-contain" />
            <div className="leading-tight">
              <p className="text-sm font-bold text-blue-800 sm:text-base">Order Staff</p>
              <p className="text-[11px] font-medium text-slate-500">{user?.name} · Sri Lakshmi Agencies</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {customer && (
              <button
                data-testid="staff-open-cart-button"
                onClick={() => setCartOpen(true)}
                className="relative grid h-10 w-10 place-items-center rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                aria-label="Open cart"
              >
                <ShoppingCart className="h-5 w-5" />
                {count > 0 && (
                  <span data-testid="staff-cart-count" className="absolute -right-1.5 -top-1.5 grid h-5 min-w-5 place-items-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-white">
                    {count}
                  </span>
                )}
              </button>
            )}
            <button
              data-testid="staff-logout-button"
              onClick={handleLogout}
              className="grid h-10 w-10 place-items-center rounded-lg border border-slate-200 text-slate-500 hover:text-red-500"
              aria-label="Logout"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Top-level nav: New Order / My Orders */}
        <nav className="mx-auto max-w-6xl px-2 pb-2">
          <div className="flex gap-1">
            <button
              data-testid="staff-view-order"
              onClick={goNewOrder}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                view === "order" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
              }`}
            >
              <PlusCircle className="h-4 w-4" /> New Order
            </button>
            <button
              data-testid="staff-view-myorders"
              onClick={goMyOrders}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                view === "myorders" ? "bg-blue-600 text-white" : "text-slate-600 hover:bg-blue-50 hover:text-blue-700"
              }`}
            >
              <ClipboardList className="h-4 w-4" /> My Orders
            </button>
          </div>
        </nav>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-6">
        {view === "myorders" ? (
          <StaffMyOrders refreshKey={myOrdersKey} />
        ) : !customer ? (
          <div data-testid="staff-search-panel" className="mx-auto max-w-lg">
            <div className="rounded-2xl border border-blue-100 bg-white p-6 shadow-sm">
              <h2 className="text-lg font-bold text-slate-800">Find a customer</h2>
              <p className="mt-1 text-sm text-slate-500">Search by mobile number to place an order on their behalf.</p>
              <form onSubmit={search} className="mt-4 flex gap-2">
                <div className="relative flex-1">
                  <Phone className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-400" />
                  <input
                    data-testid="staff-search-phone"
                    type="tel" inputMode="numeric" value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="Customer mobile number"
                    className="w-full rounded-lg border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <button
                  data-testid="staff-search-button" type="submit" disabled={searching}
                  className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-70"
                >
                  {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Search
                </button>
              </form>
              {error && <p data-testid="staff-search-error" className="mt-3 text-sm font-medium text-red-600">{error}</p>}

              {results !== null && (
                <div className="mt-4 space-y-2">
                  {results.length === 0 ? (
                    <p className="py-6 text-center text-sm text-slate-400">No customer found for that number.</p>
                  ) : (
                    results.map((c) => (
                      <div key={c.id} data-testid={`staff-customer-result-${c.id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-slate-800">{c.shop_name || c.name || "—"}</p>
                          <p className="text-xs text-slate-500">{c.name} · {c.phone}{c.place ? ` · ${c.place}` : ""}</p>
                          <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${STATUS_STYLES[c.status] || "bg-slate-100 text-slate-500"}`}>{c.status}</span>
                        </div>
                        <button
                          data-testid={`staff-select-customer-${c.id}`}
                          onClick={() => selectCustomer(c)}
                          className="flex shrink-0 items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700"
                        >
                          <UserCheck className="h-3.5 w-3.5" /> Select
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Selected customer banner */}
            <div data-testid="staff-selected-customer" className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-base font-bold text-slate-800">
                  <Store className="h-4 w-4 text-blue-500" /> {customer.shop_name || customer.name}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1"><UserCheck className="h-3.5 w-3.5 text-blue-400" />{customer.name}</span>
                  <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-blue-400" />{customer.phone}</span>
                  {customer.place && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5 text-blue-400" />{customer.place}</span>}
                </div>
                {customer.address && <p className="mt-0.5 text-xs text-slate-400">{customer.address}</p>}
              </div>
              <button
                data-testid="staff-change-customer"
                onClick={changeCustomer}
                className="flex items-center gap-1.5 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Change customer
              </button>
            </div>

            {/* Our Brands / Products / Offers tabs */}
            <div className="mb-4 flex gap-1">
              {[{ k: "brands", l: "Our Brands", Icon: Store }, { k: "products", l: "Products", Icon: Package }, { k: "offers", l: "Offers", Icon: Tag }].map(({ k, l, Icon }) => (
                <button
                  key={k}
                  data-testid={`staff-tab-${k}`}
                  onClick={() => selectTab(k)}
                  className={`flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    tab === k ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                >
                  <Icon className="h-4 w-4" /> {l}
                </button>
              ))}
            </div>

            {tab === "brands" && <BrandsSection onSelectBrand={goProductsForBrand} />}
            {tab === "products" && (
              <ProductsSection mode="products" brandFilter={brandFilter} onClearBrand={() => window.history.back()} />
            )}
            {tab === "offers" && <ProductsSection mode="offers" />}
          </>
        )}
      </main>

      {customer && (
        <CartDrawer
          open={cartOpen}
          onClose={() => setCartOpen(false)}
          endpoint="/staff/orders"
          extraPayload={{ customer_id: customer.id }}
          headerLabel={`Order for ${customer.shop_name || customer.name}`}
          successText={`Order placed for ${customer.shop_name || customer.name}. It now appears in My Orders & Admin → Orders.`}
          onOrderPlaced={() => setMyOrdersKey((k) => k + 1)}
        />
      )}
    </div>
  );
}
